import { useCallback, useEffect, useRef, useState } from "react";
import { BOOKING_LIMITS_ERROR, getBookingLimits } from "../services/bookingLimits";

// One page snapshot shared by navigation and its content; never cache across accounts/routes.
export const useFundEligibility = (firebase, uid, pathname, accountTier, accountStatus) => {
  const [state, setState] = useState({ status: "loading", data: null, kyc: null, error: "" });
  const generation = useRef(0);
  const identity = `${uid || ""}:${pathname}:${accountTier}:${accountStatus}`;
  const currentIdentity = useRef(identity);
  currentIdentity.current = identity;

  const refresh = useCallback(async () => {
    const request = ++generation.current;
    // Revalidating the same page keeps its last good snapshot so focus refreshes don't disable actions.
    setState((previous) => previous.identity === identity && previous.status === "ready"
      ? previous : { identity, status: "loading", data: null, kyc: null, error: "" });
    try {
      if (!uid || !firebase) throw new Error(BOOKING_LIMITS_ERROR);
      const [data, kycDoc] = await Promise.all([
        getBookingLimits(),
        firebase.db.collection("users").doc(uid).collection("KYC").doc("KYC").get({ source: "server" }),
      ]);
      if (request !== generation.current || identity !== currentIdentity.current) return null;
      const snapshot = { identity, status: "ready", data, kyc: kycDoc.data() || {}, error: "" };
      setState(snapshot);
      return snapshot;
    } catch (error) {
      if (request !== generation.current || identity !== currentIdentity.current) return null;
      setState({ identity, status: "error", data: null, kyc: null,
        error: error?.response?.status === 401
          ? "Your session has expired. Please sign in again to confirm your investment allowance."
          : BOOKING_LIMITS_ERROR });
      return null;
    }
  }, [firebase, uid, identity]);

  useEffect(() => {
    refresh();
    window.addEventListener("focus", refresh);
    return () => {
      generation.current++;
      window.removeEventListener("focus", refresh);
    };
  }, [refresh]);

  return {
    uid,
    ...(state.identity === identity ? state : { status: "loading", data: null, kyc: null, error: "" }),
    refresh,
  };
};
